import { useEffect, useMemo, useState } from 'react';
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  CreditCard,
  MessageCircle,
  PawPrint,
  RefreshCw,
  ShoppingCart
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getDashboardData } from '../api/dashboard';
import logoVetSuite from '../assets/logo_vet_suite_v2.png';
import Button from '../components/Button';
import { LoadingState } from '../components/PageState';
import useAuthStore from '../store/authStore';
import { currency, time } from '../utils/format';

const teal = '#26B4A8';
const navy = '#0F4C81';
const amber = '#F59E0B';

const emptyDashboard = {
  summary: {
    today_appointments: 0,
    pending_vaccines: 0,
    near_reward_customers: 0
  },
  kpis: [],
  business_evolution: [],
  loyalty: {
    paws_required: 14,
    total_customers: 0,
    one_visit_away: 0,
    average_paws: 0,
    buckets: []
  },
  contacts: [],
  top_products: [],
  pet_types: [],
  today_agenda: [],
  attendance: {
    reservations: 0,
    attended: 0,
    missed: 0,
    attendance_rate: 0
  },
  customer_mix: {
    new_customers: 0,
    recurrent_customers: 0
  },
  heatmap: {
    best_slot: null,
    days: []
  }
};

const kpiIcons = {
  money: CreditCard,
  calendar: Calendar,
  cart: ShoppingCart,
  paw: PawPrint
};

function number(value) {
  return new Intl.NumberFormat('es-CL').format(Number(value || 0));
}

function formatMetric(metric) {
  if (metric?.format === 'currency') {
    return currency(metric.value);
  }

  return number(metric?.value);
}

function getGreeting() {
  const hour = new Date().getHours();

  if (hour < 12) return 'Buenos días';
  if (hour < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

function displayName(user) {
  const name = user?.full_name || user?.name || user?.display_name;

  if (!name) {
    return 'Dra. Carolina';
  }

  return String(name).split(' ')[0];
}

function smoothPath(points) {
  if (points.length < 2) return '';

  return points.reduce((path, point, index) => {
    if (index === 0) {
      return `M ${point.x} ${point.y}`;
    }

    const previous = points[index - 1];
    const controlDistance = (point.x - previous.x) / 2;

    return `${path} C ${previous.x + controlDistance} ${previous.y}, ${point.x - controlDistance} ${point.y}, ${point.x} ${point.y}`;
  }, '');
}

const Card = ({ children, className = '' }) => (
  <section className={`rounded-lg border border-gray-200 bg-white ${className}`}>
    {children}
  </section>
);

const KpiCard = ({ metric }) => {
  const Icon = kpiIcons[metric.icon] || CreditCard;
  const trend = Number(metric.trend || 0);
  const isPositive = trend >= 0;
  const TrendIcon = isPositive ? ChevronUp : ChevronDown;

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">{metric.label}</p>
          <p className="mt-2 truncate text-2xl font-bold text-gray-950">{formatMetric(metric)}</p>
          <div className={`mt-3 flex items-center gap-1 text-sm font-semibold ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
            <TrendIcon className="h-4 w-4" />
            <span>{Math.abs(trend)}%</span>
          </div>
        </div>
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
};

const BusinessEvolutionChart = ({ data }) => {
  const chartData = data?.length ? data : [];
  const width = 760;
  const height = 260;
  const paddingX = 46;
  const paddingY = 34;
  const plotWidth = width - paddingX * 2;
  const plotHeight = height - paddingY * 2;
  const series = [
    { key: 'income', label: 'Ingresos', color: teal },
    { key: 'reservations', label: 'Reservas', color: navy },
    { key: 'online_sales', label: 'Ventas online', color: amber }
  ];

  const paths = series.map((item) => {
    const values = chartData.map((row) => Number(row[item.key] || 0));
    const max = Math.max(...values, 1);
    const points = chartData.map((row, index) => {
      const x = paddingX + (chartData.length === 1 ? plotWidth / 2 : (index * plotWidth) / (chartData.length - 1));
      const normalized = Number(row[item.key] || 0) / max;
      const y = paddingY + plotHeight - normalized * plotHeight;

      return { x, y };
    });

    return {
      ...item,
      path: smoothPath(points),
      points
    };
  });

  return (
    <Card className="p-5">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">Evolución del Negocio</p>
          <h2 className="text-xl font-bold text-gray-950">Ingresos, reservas y tienda online</h2>
        </div>
        <div className="flex flex-wrap gap-3">
          {series.map((item) => (
            <div key={item.key} className="flex items-center gap-2 text-sm text-gray-600">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
              {item.label}
            </div>
          ))}
        </div>
      </div>
      <div className="overflow-hidden">
        <svg viewBox={`0 0 ${width} ${height}`} className="h-[260px] w-full">
          {[0, 1, 2, 3].map((line) => {
            const y = paddingY + (plotHeight * line) / 3;

            return (
              <line
                key={line}
                x1={paddingX}
                x2={width - paddingX}
                y1={y}
                y2={y}
                stroke="#E5E7EB"
                strokeWidth="1"
              />
            );
          })}
          {paths.map((item) => (
            <path
              key={item.key}
              d={item.path}
              fill="none"
              stroke={item.color}
              strokeLinecap="round"
              strokeWidth="4"
            />
          ))}
          {paths.map((item) => item.points.map((point, index) => (
            <circle key={`${item.key}-${index}`} cx={point.x} cy={point.y} r="4" fill="white" stroke={item.color} strokeWidth="2.5" />
          )))}
          {chartData.map((row, index) => {
            const x = paddingX + (chartData.length === 1 ? plotWidth / 2 : (index * plotWidth) / (chartData.length - 1));

            return (
              <text key={row.month || row.label} x={x} y={height - 8} textAnchor="middle" className="fill-gray-500 text-[13px]">
                {row.label}
              </text>
            );
          })}
        </svg>
      </div>
    </Card>
  );
};

const LoyaltyMap = ({ loyalty }) => {
  const pawsRequired = Number(loyalty?.paws_required || 14);
  const filled = Math.min(Math.round(Number(loyalty?.average_paws || 0)), pawsRequired);
  const buckets = loyalty?.buckets || [];

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-gray-500">Mapa de Fidelización</p>
          <h2 className="text-xl font-bold text-gray-950">Clientes Patitas</h2>
        </div>
        <PawPrint className="h-6 w-6 text-teal-600" />
      </div>
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: pawsRequired }, (_, index) => (
          <div
            key={index}
            className={`flex aspect-square items-center justify-center rounded-md border ${
              index < filled ? 'border-teal-200 bg-teal-50 text-teal-700' : 'border-gray-200 bg-gray-50 text-gray-300'
            }`}
          >
            <PawPrint className="h-4 w-4" />
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-lg bg-gray-950 p-4 text-white">
        <p className="text-sm text-gray-300">A una visita de una consulta gratis</p>
        <p className="mt-1 text-3xl font-bold">{number(loyalty?.one_visit_away)}</p>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {buckets.map((bucket) => (
          <div key={bucket.key} className="rounded-md border border-gray-200 p-3">
            <p className="text-xs text-gray-500">{bucket.label}</p>
            <p className="mt-1 text-lg font-bold text-gray-950">{number(bucket.value)}</p>
          </div>
        ))}
      </div>
    </Card>
  );
};

const ContactsWidget = ({ contacts }) => (
  <Card className="p-5">
    <div className="mb-4 flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-gray-500">Clientes que debes contactar hoy</p>
        <h2 className="text-xl font-bold text-gray-950">Acciones comerciales</h2>
      </div>
      <MessageCircle className="h-6 w-6 text-green-600" />
    </div>
    <div className="space-y-3">
      {(contacts || []).map((contact) => (
        <div key={contact.customer_id} className="rounded-md border border-gray-200 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-semibold text-gray-950">{contact.name}</p>
              <p className="text-sm text-gray-600">{contact.detail}</p>
            </div>
            {contact.whatsapp_url ? (
              <a
                href={contact.whatsapp_url}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 rounded-md bg-green-50 px-3 py-2 text-sm font-semibold text-green-700 hover:bg-green-100"
              >
                WhatsApp
              </a>
            ) : (
              <span className="shrink-0 rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-400">Sin teléfono</span>
            )}
          </div>
          <p className="mt-2 text-xs font-medium uppercase text-gray-400">{contact.reason}</p>
        </div>
      ))}
      {(!contacts || contacts.length === 0) && (
        <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          No hay contactos urgentes para hoy
        </div>
      )}
    </div>
  </Card>
);

const HorizontalBars = ({ title, subtitle, rows, valueKey = 'percentage' }) => (
  <Card className="p-5">
    <div className="mb-4">
      <p className="text-sm font-medium text-gray-500">{subtitle}</p>
      <h2 className="text-xl font-bold text-gray-950">{title}</h2>
    </div>
    <div className="space-y-4">
      {(rows || []).map((row) => (
        <div key={row.name || row.label}>
          <div className="mb-1 flex items-center justify-between gap-3 text-sm">
            <span className="truncate font-semibold text-gray-800">{row.name || row.label}</span>
            <span className="shrink-0 text-gray-500">{row.quantity ? `${number(row.quantity)} uds` : number(row.value)}</span>
          </div>
          <div className="h-3 rounded-full bg-gray-100">
            <div
              className="h-3 rounded-full bg-teal-500"
              style={{ width: `${Math.max(Number(row[valueKey] || 0), 4)}%` }}
            />
          </div>
        </div>
      ))}
      {(!rows || rows.length === 0) && (
        <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          Sin datos para mostrar
        </div>
      )}
    </div>
  </Card>
);

const PetTypesDonut = ({ data }) => {
  const dogs = data?.find((item) => item.key === 'dogs')?.percentage || 0;
  const cats = data?.find((item) => item.key === 'cats')?.percentage || 0;
  const others = Math.max(100 - dogs - cats, 0);
  const gradient = `conic-gradient(${teal} 0 ${dogs}%, ${navy} ${dogs}% ${dogs + cats}%, ${amber} ${dogs + cats}% 100%)`;

  return (
    <Card className="p-5">
      <p className="text-sm font-medium text-gray-500">Tipos de pacientes</p>
      <h2 className="text-xl font-bold text-gray-950">Mascotas atendidas</h2>
      <div className="mt-5 flex items-center gap-5">
        <div className="relative h-32 w-32 shrink-0 rounded-full" style={{ background: gradient }}>
          <div className="absolute inset-5 rounded-full bg-white" />
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          {(data || []).map((item) => {
            const color = item.key === 'dogs' ? teal : item.key === 'cats' ? navy : amber;

            return (
              <div key={item.key} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex min-w-0 items-center gap-2 font-semibold text-gray-800">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                  {item.label}
                </span>
                <span className="shrink-0 text-gray-500">{item.percentage || 0}%</span>
              </div>
            );
          })}
          {(!data || data.length === 0) && <p className="text-sm text-gray-500">Sin pacientes registrados</p>}
          {others < 0 && null}
        </div>
      </div>
    </Card>
  );
};

const TodayAgenda = ({ agenda }) => (
  <Card className="p-5">
    <p className="text-sm font-medium text-gray-500">Calendario inteligente</p>
    <h2 className="text-xl font-bold text-gray-950">Hoy</h2>
    <div className="mt-4 space-y-3">
      {(agenda || []).map((item) => (
        <div key={item.id} className="flex items-center gap-3 rounded-md border border-gray-200 p-3">
          <div className="w-14 shrink-0 rounded-md bg-gray-950 px-2 py-1 text-center text-sm font-bold text-white">
            {time(item.starts_at)}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-gray-950">{item.pet_name || 'Mascota'}</p>
            <p className="truncate text-sm text-gray-500">{item.service_name || item.customer_name || '-'}</p>
          </div>
        </div>
      ))}
      {(!agenda || agenda.length === 0) && (
        <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          No quedan citas próximas para hoy
        </div>
      )}
    </div>
  </Card>
);

const AttendanceGauge = ({ attendance }) => {
  const rate = Math.max(Math.min(Number(attendance?.attendance_rate || 0), 100), 0);
  const gradient = `conic-gradient(${teal} 0 ${rate}%, #E5E7EB ${rate}% 100%)`;

  return (
    <Card className="p-5">
      <p className="text-sm font-medium text-gray-500">Recordatorios efectivos</p>
      <h2 className="text-xl font-bold text-gray-950">Asistencia</h2>
      <div className="mt-5 flex items-center gap-5">
        <div className="relative h-32 w-32 shrink-0 rounded-full" style={{ background: gradient }}>
          <div className="absolute inset-5 flex items-center justify-center rounded-full bg-white">
            <span className="text-2xl font-bold text-gray-950">{rate}%</span>
          </div>
        </div>
        <div className="grid flex-1 grid-cols-1 gap-2 text-sm">
          <p className="flex justify-between gap-3"><span className="text-gray-500">Reservas</span><b>{number(attendance?.reservations)}</b></p>
          <p className="flex justify-between gap-3"><span className="text-gray-500">Asistieron</span><b>{number(attendance?.attended)}</b></p>
          <p className="flex justify-between gap-3"><span className="text-gray-500">Faltaron</span><b>{number(attendance?.missed)}</b></p>
        </div>
      </div>
    </Card>
  );
};

const CustomerMix = ({ mix }) => {
  const rows = [
    { label: 'Nuevos', value: Number(mix?.new_customers || 0), color: teal },
    { label: 'Recurrentes', value: Number(mix?.recurrent_customers || 0), color: navy }
  ];
  const max = Math.max(...rows.map((row) => row.value), 1);

  return (
    <Card className="p-5">
      <p className="text-sm font-medium text-gray-500">Widget comercial</p>
      <h2 className="text-xl font-bold text-gray-950">Clientes Nuevos vs Recurrentes</h2>
      <div className="mt-5 space-y-4">
        {rows.map((row) => (
          <div key={row.label}>
            <div className="mb-1 flex justify-between text-sm">
              <span className="font-semibold text-gray-800">{row.label}</span>
              <span className="text-gray-500">{number(row.value)}</span>
            </div>
            <div className="h-4 rounded-full bg-gray-100">
              <div
                className="h-4 rounded-full"
                style={{ width: `${Math.max((row.value / max) * 100, 4)}%`, backgroundColor: row.color }}
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const Heatmap = ({ heatmap }) => (
  <Card className="p-5">
    <p className="text-sm font-medium text-gray-500">Horarios más demandados</p>
    <h2 className="text-xl font-bold text-gray-950">Mapa de Calor</h2>
    <div className="mt-5 space-y-3">
      {(heatmap?.days || []).map((day) => (
        <div key={day.key} className="grid grid-cols-[92px_1fr_44px] items-center gap-3 text-sm">
          <span className="font-semibold text-gray-700">{day.label}</span>
          <div className="h-4 rounded-sm bg-gray-100">
            <div
              className="h-4 rounded-sm bg-teal-500"
              style={{ width: `${Math.max(Number(day.intensity || 0), day.value ? 8 : 2)}%` }}
            />
          </div>
          <span className="text-right text-gray-500">{number(day.value)}</span>
        </div>
      ))}
    </div>
    <div className="mt-5 rounded-lg bg-amber-50 p-4 text-amber-900">
      <p className="text-sm font-semibold">Hora más rentable</p>
      <p className="mt-1 text-lg font-bold">{heatmap?.best_slot?.label || 'Sin datos aún'}</p>
    </div>
  </Card>
);

const Dashboard = () => {
  const { user } = useAuthStore();
  const [data, setData] = useState(emptyDashboard);
  const [loading, setLoading] = useState(true);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await getDashboardData();
      setData({ ...emptyDashboard, ...response });
    } catch (error) {
      toast.error(error.friendlyMessage || 'Error cargando dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const topProductRows = useMemo(() => data.top_products || [], [data.top_products]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Card className="p-6">
          <LoadingState rows={3} />
        </Card>
        <LoadingState rows={8} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {(data.kpis || []).map((metric) => (
          <KpiCard key={metric.key} metric={metric} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.55fr)]">
        <BusinessEvolutionChart data={data.business_evolution} />
        <LoyaltyMap loyalty={data.loyalty} />
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <ContactsWidget contacts={data.contacts} />
        <HorizontalBars title="Top Productos" subtitle="Tienda Online" rows={topProductRows} />
        <PetTypesDonut data={data.pet_types} />
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-4">
        <TodayAgenda agenda={data.today_agenda} />
        <AttendanceGauge attendance={data.attendance} />
        <CustomerMix mix={data.customer_mix} />
        <Heatmap heatmap={data.heatmap} />
      </div>
    </div>
  );
};

export default Dashboard;
